/**
 * PostHog error monitoring for the spacecode CLI (replaces Sentry, 2026-10-09).
 *
 * Dependency-free: posts events to PostHog's `/capture/` endpoint with `fetch`.
 * Opt-in: no-op unless `POSTHOG_API_KEY` is set (never hardcoded). Send
 * failures are logged to stderr only when SPACECODE_DEBUG is set, since a CLI
 * must not spam users because telemetry is unreachable.
 */

type Level = 'debug' | 'info' | 'warning' | 'error';

let apiKey: string | undefined;
let host = 'https://us.i.posthog.com';
const breadcrumbs: Array<Record<string, unknown>> = [];

export function initPostHog(): void {
  apiKey = process.env.POSTHOG_API_KEY || undefined;
  host = (process.env.POSTHOG_HOST || host).replace(/\/+$/, '');
  if (process.env.NODE_ENV === 'development' && !process.env.POSTHOG_ENABLE) {
    apiKey = undefined;
  }
}

function send(event: string, properties: Record<string, unknown>): void {
  if (!apiKey) return;
  // Observational telemetry: fire-and-forget, never blocks or throws.
  fetch(`${host}/capture/`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      api_key: apiKey,
      event,
      distinct_id: 'spacecode-cli',
      timestamp: new Date().toISOString(),
      properties: {
        service: 'cli',
        $lib: 'spacecode-monitoring',
        environment: process.env.NODE_ENV || 'production',
        breadcrumbs: breadcrumbs.slice(-50),
        ...properties,
      },
    }),
  }).catch((err) => {
    if (process.env.SPACECODE_DEBUG) console.error('[posthog] capture failed:', err);
  });
}

export function logInfo(message: string, attributes?: Record<string, unknown>): void {
  send('cli_log', { level: 'info', message, ...attributes });
}

export function logWarning(message: string, attributes?: Record<string, unknown>): void {
  send('cli_log', { level: 'warning', message, ...attributes });
}

export function logError(message: string, attributes?: Record<string, unknown>): void {
  send('cli_log', { level: 'error', message, ...attributes });
}

export function captureException(error: Error, context?: Record<string, unknown>): void {
  send('$exception', {
    $exception_list: [{ type: error.name, value: error.message, stacktrace: { type: 'raw', frames: [], raw: error.stack } }],
    $exception_level: 'error',
    ...context,
  });
}

export function addBreadcrumb(message: string, category?: string, level?: Level): void {
  breadcrumbs.push({ message, category: category || 'cli', level: level || 'info', ts: new Date().toISOString() });
  if (breadcrumbs.length > 50) breadcrumbs.shift();
}
